// A tab left open across a deploy runs the old client against the new
// server, whose requests it may no longer match. The build a page runs is
// named by its module entry script (hashed per build); when the page the
// server now serves loads another one, the tab offers a reload.
//
// Checked when the live events reconnect (a deploy restarts the server)
// and when the tab comes back into view.
import { MOCK } from '../mock/flag';

const ENTRY = /<script\b[^>]*\btype="module"[^>]*\bsrc="([^"]+)"/;

/** The module entry script an HTML page loads, or null. */
export function entryOf(html: string): string | null {
  return ENTRY.exec(html)?.[1] ?? null;
}

/** Whether `served` (the page the server serves now) loads another build than `own`; unknown on either side is not. */
export function isNewBuild(own: string | null, served: string): boolean {
  const next = entryOf(served);
  return own !== null && next !== null && next !== own;
}

/** The entry this page loaded with. */
const own: string | null =
  typeof document === 'undefined' ? null : (document.querySelector('script[type="module"][src]')?.getAttribute('src') ?? null);

/** Least time between two checks. */
const CHECK_EVERY_MS = 60_000;

class Build {
  /** The server serves a newer client than this tab runs: reload to use it. */
  stale = $state(false);
  private lastCheck = 0;

  /** Ask the server which build it serves (at most once a minute; never in dev or mock mode). */
  async check(): Promise<void> {
    if (this.stale || own === null || MOCK || import.meta.env.DEV) return;
    const now = Date.now();
    if (now - this.lastCheck < CHECK_EVERY_MS) return;
    this.lastCheck = now;
    try {
      const res = await fetch(import.meta.env.BASE_URL, { cache: 'no-store', credentials: 'same-origin' });
      if (res.ok && isNewBuild(own, await res.text())) this.stale = true;
    } catch {
      /* offline or restarting: the next check asks again */
    }
  }
}

export const build = new Build();
